import { process, State } from '@progress/kendo-data-query';
import { Grid, GridCell, GridCellProps, GridColumn, GridDataStateChangeEvent, GridHeaderCell, GridHeaderCellProps, GridNoRecords, GridProps, GridRowClickEvent } from '@progress/kendo-react-grid';
import invariant from 'invariant';
import { isEqual, kebabCase } from 'lodash';
import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer, Size } from 'react-virtualized';
import { intl, translate } from '../../../common/intl';
import StyledKendoGrid from '../../components/StyledKendoGrid/StyledKendoGrid';
import { StyledKendoGridProps } from '../StyledKendoGrid/StyledKendoGrid.types';
import Loader from '../common/kendo/Loader';
import { Mode, MODE_FIELD, SOLO_OPERATORS } from './constants';
import { ColumnDefinition, CustomGridCellProps, CustomGridHeaderCellProps, DataGridProps, GenericColumnDefinition, WidthCalculator } from './KendoDataGrid.types';
import { useFitColumns } from './useFitColumns';

const DEFAULT_DATA_STATE: State = {
  filter: {
    logic: 'and',
    filters: [],
  },
};

const CustomGridCell: React.FC<CustomGridCellProps> = ({ column, field, onClick, ...props }) => {
  const onCellClick = useCallback((e: { dataItem: unknown }) => {
    onClick?.({
      ...e as GridRowClickEvent,
      dataItem: props.dataItem,
    });
  }, [onClick, props.dataItem]);
  const renderCell = useCallback<NonNullable<GridCellProps['render']>>((element, cellProps) => {
    if (React.isValidElement(element)) {
      return React.cloneElement(
        element,
        {
          ['data-cy' as string]: `cell-${kebabCase(column.id)}`,
        },
        column.template
          ? column.template({ ...cellProps, column, field, onClick: onCellClick })
          : undefined,
      );
    }
    return element;
  }, [column, field, onCellClick]);
  return (
    column.cell
      ? <column.cell {...props} column={column} field={field} onClick={onCellClick} />
      : (
        <GridCell
          {...props}
          field={field}
          render={renderCell}
        />
      )
  );
};

const CustomHeaderCell: React.FC<CustomGridHeaderCellProps> = ({ column, ...props }) => {
  if (column.headerCell) {
    return <column.headerCell {...props} column={column} />;
  }

  return column.title ? <GridHeaderCell {...props} /> : null;
};

function isGenericColumns (columns: GenericColumnDefinition['children']): columns is GenericColumnDefinition[] {
  return Array.isArray(columns) && columns.every((item) => 'id' in item);
}

const GridContext = React.createContext<{
  columnsByField: Map<string | undefined, GenericColumnDefinition>
  onRowClick?: GridProps['onRowClick']
}>({
  columnsByField: new Map(),
  onRowClick: undefined,
});

function processSchema<T> (schema: Array<ColumnDefinition<T>>): Array<ColumnDefinition<T>> {
  return schema.map((column) => {
    const field = column.field ?? `custom-${column.id}`;

    return {
      ...column,
      field,
    };
  });
}

const CellWrapper = (props: GridCellProps) => {
  const { columnsByField, onRowClick } = useContext(GridContext);

  invariant(props.field, 'Field is required when using custom cell');

  const column = columnsByField.get(props.field);

  invariant(column, `Column with field ${props.field} not found`);

  if (!column) {
    return null;
  }
  return (
    <CustomGridCell
      {...props}
      column={column}
      field={props.field}
      onClick={onRowClick}
    />
  );
};

type HeaderAdapters = WeakMap<GenericColumnDefinition, React.ComponentType<GridHeaderCellProps>>;

const createColumnMetadata = (columns: GenericColumnDefinition[], headers: HeaderAdapters) => {
  const columnsByField = new Map<string | undefined, GenericColumnDefinition>();
  const visit = (definitions: GenericColumnDefinition[]) => {
    definitions.forEach((definition) => {
      if (!columnsByField.has(definition.field)) {
        columnsByField.set(definition.field, definition);
      }
      if (!headers.has(definition)) {
        headers.set(definition, (props) => {
          const { children, ...column } = definition;
          return <CustomHeaderCell {...props} column={column} />;
        });
      }
      const { children } = definition;
      if (isGenericColumns(children)) {
        visit(children);
      }
    });
  };
  visit(columns);
  return columnsByField;
};

const renderColumns = (
  columns: GenericColumnDefinition[],
  headers: HeaderAdapters,
  gridWidth: number,
  calculateColumWidth?: WidthCalculator,
) => {
  return columns.filter(({ hidden }) => !hidden).map((definition) => {
    const { children, ...column } = definition;
    const shouldUseCustomCell = column.cell ?? column.template;

    return (
      <GridColumn
        {...column}
        key={column.id}
        title={translate(column.title)}
        cell={shouldUseCustomCell ? CellWrapper : undefined}
        headerCell={headers.get(definition)}
        resizable={column.resizable && !column.fitContent}
        width={
          calculateColumWidth && typeof column.width === 'number'
            ? calculateColumWidth(column.width, gridWidth)
            : column.width
        }
      >
        {
          isGenericColumns(children)
            ? renderColumns(children, headers, gridWidth, calculateColumWidth)
            : children
        }
      </GridColumn>
    );
  });
};

interface GridWithColumnsProps extends StyledKendoGridProps {
  columns: GenericColumnDefinition[]
  headers: HeaderAdapters
  gridWidth: number
  gridHeight?: number
  calculateColumWidth?: WidthCalculator
  gridRef: React.MutableRefObject<Grid | undefined>
}

// Hooks belong here so AutoSizer updates widths without replacing header types.
const GridWithColumns: React.FC<PropsWithChildren<GridWithColumnsProps>> = ({
  columns, headers, gridWidth, gridHeight, calculateColumWidth, gridRef, children, ...props
}) => {
  const { locale, messages } = intl;
  const columnElements = useMemo(
    () => renderColumns(columns, headers, gridWidth, calculateColumWidth),
    // translate reads the locale and message catalog indirectly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, headers, gridWidth, calculateColumWidth, locale, messages],
  );
  const autosizedColumns = useMemo(
    () => columns.filter((column) => column.fitContent).map((column) => column.id),
    [columns],
  );
  useFitColumns(gridRef, props.containerRef, autosizedColumns, columnElements, props.data, gridWidth, gridHeight);

  return (
    <StyledKendoGrid {...props} ref={gridRef}>
      {columnElements}
      {children}
    </StyledKendoGrid>
  );
};

function KendoDataGrid<T> ({
  data,
  defaultDataState = DEFAULT_DATA_STATE,
  filter,
  group,
  sort,
  skipDataProcessing,
  loading,
  schema,
  children,
  fullWidth,
  fullHeight,
  fallback,
  onRowClick,
  onColumnReorder,
  onDataStateChange,
  calculateColumWidth,
  ref,
  containerRef,
  style,
  onResize,
  ...props
}: PropsWithChildren<DataGridProps<T>>): React.ReactElement {
  const fallbackGridRef = useRef<Grid>();
  const fallbackContainerRef = useRef<HTMLDivElement>(null);
  const gridRef = ref ?? fallbackGridRef;
  const gridContainerRef = containerRef ?? fallbackContainerRef;
  const normalizedSchema = useMemo(() => processSchema(schema), [schema]);
  const synchronizedSchema = useRef(normalizedSchema);
  const [columnsSchema, setColumnsSchema] = useState(() => normalizedSchema);
  const [dataState, setDataState] = useState<State>(() => ({
    ...defaultDataState,
    ...filter && { filter },
    ...group && { group },
    ...sort && { sort },
  }));

  useEffect(() => {
    if (synchronizedSchema.current !== normalizedSchema) {
      synchronizedSchema.current = normalizedSchema;
      setColumnsSchema(normalizedSchema);
    }
  }, [normalizedSchema]);

  useEffect(() => {
    setDataState((prev) => {
      if ((!filter || isEqual(prev.filter, filter))
        && (!group || isEqual(prev.group, group))
        && (!sort || isEqual(prev.sort, sort))) {
        return prev;
      }
      return {
        ...prev,
        ...filter && { filter },
        ...group && { group },
        ...sort && { sort },
      };
    });
  }, [filter, group, sort]);

  const resultData = useMemo(
    () => {
      if (skipDataProcessing) {
        return data;
      }
      const finalDataState: State = {
        ...dataState,
        filter: {
          logic: 'or',
          filters: [
            {
              logic: dataState.filter?.logic ?? 'and',
              filters: dataState.filter?.filters ?? [],
            },
            {
              field: MODE_FIELD,
              operator: 'eq',
              value: Mode.add,
            },
            {
              field: MODE_FIELD,
              operator: 'eq',
              value: Mode.edit,
            },
          ],
        },
      };
      const processedData = data ? process(data, finalDataState) : [];
      return processedData;
    },
    [data, skipDataProcessing, dataState],
  );

  const [headers] = useState<HeaderAdapters>(() => new WeakMap());
  const columnsByField = useMemo(
    () => createColumnMetadata(columnsSchema as GenericColumnDefinition[], headers),
    [columnsSchema, headers],
  );
  const contextValue = useMemo(() => ({ columnsByField, onRowClick }), [columnsByField, onRowClick]);

  const updateDataState = useCallback((event: GridDataStateChangeEvent) => {
    if (event.dataState.filter?.filters) {
      event.dataState.filter.filters = event.dataState.filter.filters
        .filter((item) => {
          if ('value' in item && 'operator' in item) {
            return (
              item.value !== undefined
              || (typeof item.operator === 'string' && SOLO_OPERATORS.includes(item.operator))
            );
          }

          return true;
        });
    }

    setDataState(
      onDataStateChange
        ? onDataStateChange(event)
        : event.dataState,
    );
  }, [onDataStateChange]);

  const reorderColumns = useCallback<NonNullable<GridProps['onColumnReorder']>>((event) => {
    if (onColumnReorder) {
      return setColumnsSchema(onColumnReorder(event));
    }
    const { columns } = event;
    setColumnsSchema((prevSchema) => prevSchema.map((column) => {
      const updatedColumn = columns.find((el) => el.id === column.id);
      return {
        ...column,
        orderIndex: updatedColumn ? updatedColumn.orderIndex : column.orderIndex,
      };
    }));
  }, [onColumnReorder]);

  // AutoSizer is pure; a fresh render callback keeps mutable caller records
  // visible on parent renders even when their array identity is unchanged.
  const renderGrid = ({ width, height }: Partial<Size> = {}) => {
    return (
      <GridContext.Provider
        value={contextValue}
      >
        <GridWithColumns
          {...props}
          columns={columnsSchema as GenericColumnDefinition[]}
          headers={headers}
          gridWidth={width ?? 0}
          gridHeight={height}
          calculateColumWidth={calculateColumWidth}
          style={{
            ...style,
            ...(fullWidth && width !== undefined ? { width: width } : {}),
            ...(fullHeight && height !== undefined ? { height: height } : {}),
          }}
          data={resultData}
          onDataStateChange={updateDataState}
          onColumnReorder={reorderColumns}
          gridRef={gridRef}
          containerRef={gridContainerRef}
          onRowClick={onRowClick}
          {...dataState}
        >
          {fallback && (
            <GridNoRecords>{fallback}</GridNoRecords>
          )}
          {children}
        </GridWithColumns>
        {loading && (
          <Loader gridRef={gridContainerRef} />
        )}
      </GridContext.Provider>
    );
  };

  if (!fullWidth && !fullHeight) {
    return renderGrid();
  }

  return (
    <AutoSizer
      disableWidth={!fullWidth}
      disableHeight={!fullHeight}
      onResize={onResize}
    >
      {(size) => renderGrid(size)}
    </AutoSizer>
  );
}

export default KendoDataGrid;
